import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import analyticsService, { ConversationAnalytics, VisitorJourneyData } from '../../services/analyticsService';
import MessageTimeline from './MessageTimeline';
import ContextTracker from './ContextTracker';

const ConversationDetail: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState<ConversationAnalytics | null>(null);
  const [visitorJourney, setVisitorJourney] = useState<VisitorJourneyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'messages' | 'context' | 'metrics'>('messages');

  useEffect(() => {
    const fetchConversationData = async () => {
      if (!conversationId) return;

      try {
        setLoading(true);

        // Get the specific conversation by ID using the enhanced API
        const conversationsResponse = await analyticsService.getConversations({
          page: 1,
          limit: 1,
          conversationId: conversationId // Use the new conversationId parameter
        } as any);

        const targetConversation = conversationsResponse.conversations[0] || null;

        if (!targetConversation) {
          toast.error('Conversation not found');
          navigate('/analytics');
          return;
        }

        setConversation(targetConversation);

        // Get visitor journey data
        const journeyData = await analyticsService.getVisitorJourney(targetConversation.visitor_uuid);
        setVisitorJourney(journeyData);

      } catch (error) {
        console.error('Failed to fetch conversation data:', error);
        toast.error('Failed to load conversation details');
      } finally {
        setLoading(false);
      }
    };

    fetchConversationData();
  }, [conversationId, navigate]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'converted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'abandoned':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return 'Ongoing';
    if (minutes < 1) return '< 1min';
    if (minutes < 60) return `${Math.round(minutes)}min`;
    const hours = Math.floor(minutes / 60);
    const remainingMins = Math.round(minutes % 60);
    return `${hours}h ${remainingMins}min`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!conversation || !visitorJourney) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Conversation not found</p>
        <button
          onClick={() => navigate('/analytics')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          ← Back to Analytics
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'messages', name: 'Messages', icon: ChatBubbleLeftRightIcon },
    { id: 'context', name: 'Context Evolution', icon: UserIcon },
    { id: 'metrics', name: 'Performance', icon: ClockIcon }
  ];

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/analytics')}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Analytics
            </button>
            <div className="h-6 border-l border-gray-300"></div>
            <h1 className="text-2xl font-bold text-gray-900">
              Conversation Detail
            </h1>
          </div>
          <div className={`px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(conversation.status)}`}>
            {conversation.status.charAt(0).toUpperCase() + conversation.status.slice(1)}
          </div>
        </div>
      </div>

      {/* Visitor Information Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Visitor Information</h3>

            <div className="space-y-4">
              {/* Visitor Identity */}
              <div className="flex items-center space-x-3">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Visitor</p>
                  <p className="font-medium text-gray-900">
                    {conversation.parent_name ||
                     conversation.visitor_email ||
                     `Visitor ${conversation.visitor_uuid.slice(0, 8)}`}
                  </p>
                </div>
              </div>

              {/* Email */}
              {(conversation.visitor_email || conversation.lead_email) && (
                <div className="flex items-center space-x-3">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">
                      {conversation.lead_email || conversation.visitor_email}
                    </p>
                  </div>
                </div>
              )}

              {/* Session Start */}
              <div className="flex items-center space-x-3">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Session Started</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(conversation.started_at)}
                  </p>
                </div>
              </div>

              {/* Duration */}
              <div className="flex items-center space-x-3">
                <ClockIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Duration</p>
                  <p className="font-medium text-gray-900">
                    {formatDuration(conversation.duration)}
                  </p>
                </div>
              </div>

              {/* Message Count */}
              <div className="flex items-center space-x-3">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Messages</p>
                  <p className="font-medium text-gray-900">
                    {conversation.message_count} total
                  </p>
                </div>
              </div>

              {/* Data Collection Status */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Information Collected</p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    {conversation.has_name ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-sm text-gray-700">Parent Name</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {conversation.has_email ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-sm text-gray-700">Email Address</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {conversation.has_child_info ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-sm text-gray-700">Child Information</span>
                  </div>
                </div>
              </div>

              {/* Lead Information if Converted */}
              {conversation.lead_id && visitorJourney.lead && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500 mb-2">Lead Information</p>
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="text-gray-500">Lead Score:</span>
                      <span className="ml-2 font-medium">{visitorJourney.lead.lead_score || 0}</span>
                    </div>
                    {visitorJourney.lead.child_name && (
                      <div className="text-sm">
                        <span className="text-gray-500">Child:</span>
                        <span className="ml-2 font-medium">{visitorJourney.lead.child_name}</span>
                      </div>
                    )}
                    {visitorJourney.lead.timeline && (
                      <div className="text-sm">
                        <span className="text-gray-500">Timeline:</span>
                        <span className="ml-2 font-medium">{visitorJourney.lead.timeline}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md">
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as typeof activeTab)}
                      className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="h-5 w-5 mr-2" />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'messages' && (
                <MessageTimeline
                  conversation={conversation}
                  visitorJourney={visitorJourney}
                />
              )}

              {activeTab === 'context' && (
                <ContextTracker
                  conversation={conversation}
                  visitorJourney={visitorJourney}
                />
              )}

              {activeTab === 'metrics' && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Performance Metrics</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500">Conversion Status</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {conversation.status === 'converted' ? 'Converted' : 'Not Converted'}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500">Engagement Score</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {Math.round((conversation.message_count / (conversation.duration || 1)) * 10) || 0}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500">Total Sessions</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {visitorJourney.visitor.total_visits}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500">Data Completion</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {Math.round(
                          ([conversation.has_name, conversation.has_email, conversation.has_child_info]
                            .filter(Boolean).length / 3) * 100
                        )}%
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationDetail;